import {
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Divider as MuiDivider,
  FormControl as MuiFormControl,
  Box,
  TextField as MuiTextField,
  Typography,
} from "@mui/material";
import Paper from "@mui/material/Paper";
// Custom Componenets
//Material UI Components and Functions
import { makeStyles } from "@mui/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import { spacing } from "@mui/system";
// Redux Components
import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { connect } from "react-redux";
import styled from "styled-components";
import {
  handleBillingProcessSubmission,
  updateBillingWizardBillInProgress,
} from "../../../../modules/billing/api/actions";
import { formatMoney, numberWithCommas } from "../../../../setup/helpers";

const CenteredContent = styled.div`
  text-align: center;
`;

const Card = styled(MuiCard)(spacing);
const Divider = styled(MuiDivider)(spacing);
const FormControl = styled(MuiFormControl)(spacing);
const TextField = styled(MuiTextField)(spacing);

// Custom Styles for property selection drop down

const useStyles = makeStyles((theme) => ({
  container: {
    display: "flex",
    flexWrap: "wrap",
  },
  selectedPropertyLocationLabel: {
    fontSize: 50,
    backgroundColor: "#fffffff",
    borderColor: "#fffffff",
  },
  loadingIconContainer: {
    display: "flex",
    alignItems: "center",
  },
  loadingIcon: {
    position: "relative",
    padding: "50px",
  },
  meterReadingSuccessColor: {
    color: "#1e4620",
  },
  meterReadingFailColor: {
    color: "#611a15",
  },
  meterReadingValue: {
    fontWeight: "bolder",
  },
  totalsSubValue: {
    fontSize: 18,
  },
}));

const TrialBillingSummary = (props) => {
  const classes = useStyles();
  const [summary, setSummary] = React.useState(null);

  // On initial page load methods:
  useEffect(() => {
    if (!props.billing.billInProgressIsLoading) {
      // Totals
      let newSummary = {
        totalCharge: 0,
        kwhChargeAmount: 0,
        tenentKWHUsage: 0,
        tenentAccountCharges: 0,
        commonAccountCharges: 0,
        commonAccountKWHUsage: 0,
        finalAccountKWHUsage: 0,
        finalAccountCharges: 0
      }

      for (var x = 0; x < props.billing.data.billInProgress.trialBillingItems.items.length; x++) {

        newSummary.totalCharge = newSummary.totalCharge + props.billing.data.billInProgress.trialBillingItems.items[x].totalCharge;
        newSummary.kwhChargeAmount = newSummary.kwhChargeAmount + props.billing.data.billInProgress.trialBillingItems.items[x].usage;
        if (props.billing.data.billInProgress.trialBillingItems.items[x].commonAccount == 1) // Should make the default just an empty string
        {
          newSummary.commonAccountCharges = newSummary.commonAccountCharges + props.billing.data.billInProgress.trialBillingItems.items[x].totalCharge;
          newSummary.commonAccountKWHUsage = newSummary.commonAccountKWHUsage + props.billing.data.billInProgress.trialBillingItems.items[x].usage;
        }
        else {
          newSummary.tenentAccountCharges = newSummary.tenentAccountCharges + props.billing.data.billInProgress.trialBillingItems.items[x].totalCharge;
          newSummary.tenentKWHUsage = newSummary.tenentKWHUsage + props.billing.data.billInProgress.trialBillingItems.items[x].usage;
        }
        if (props.billing.data.billInProgress.trialBillingItems.items[x].finalRead == 1) // Should make the default just an empty string
        {
          newSummary.finalAccountCharges = newSummary.finalAccountCharges + props.billing.data.billInProgress.trialBillingItems.items[x].totalCharge;
          newSummary.finalAccountKWHUsage = newSummary.finalAccountKWHUsage + props.billing.data.billInProgress.trialBillingItems.items[x].usage;
        }
      }
      setSummary(newSummary)
    }

  }, [props.billing.data.billInProgress.trialBillingItems, props.billing.data.billInProgress.vendorInformation, props.billing.billInProgressIsLoading]);


  return (
    <Card mb={6} elevation={3}>
      <CardContent>
        {/* Main Content (Just imports the properties meter data manager) */}
        {props.billing.isLoading || summary == null ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              gap: 4
            }}
          >
            <div className={classes.loadingIcon}>
              <CircularProgress m={2} />
            </div>
          </Box>
        ) : (
          <Box sx={{ width: '100%' }}>
            <TableContainer component={Paper} sx={{ width: '100%' }}>
              <Table className={classes.table} aria-label="simple table" sx={{ width: '100%' }}>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Typography variant="h5">Vendor Bill Value:</Typography>
                      <Typography variant="h5">
                        Total To Be Billed:
                      </Typography>
                      <Typography variant="h5">Total Profit</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h5">
                        {formatMoney(
                          props.billing.data.billInProgress.vendorInformation
                            .billTotal
                        )}
                      </Typography>
                      <Typography variant="h5">
                        {formatMoney(
                          props.billing.data.billInProgress.billingSummary
                            .totalToBeBilled
                        )}
                      </Typography>
                      <Typography variant="h5">
                        {formatMoney(
                          props.billing.data.billInProgress.billingSummary
                            .totalToBeBilled -
                          props.billing.data.billInProgress
                            .vendorInformation.billTotal
                        )}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      <Typography variant="h5">
                        Vendor Bill Usage (KWH):
                      </Typography>
                      <Typography variant="h5">
                        Usage To Be Billed (KWH):
                      </Typography>
                      <Typography
                        variant="h5"
                        className={classes.totalsSubValue}
                      >
                        KWH Usage Difference
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="h5">
                        {numberWithCommas(
                          props.billing.data.billInProgress.vendorInformation
                            .kwhUsedTotal
                        )}{" "}
                        <i>(kWh)</i>
                      </Typography>
                      <Typography variant="h5">
                        {numberWithCommas(summary.kwhChargeAmount)} <i>(kWh)</i>
                      </Typography>
                      <Typography
                        variant="h5"
                        className={classes.totalsSubValue}
                      >
                        {numberWithCommas(
                          props.billing.data.billInProgress.vendorInformation
                            .kwhUsedTotal - summary.kwhChargeAmount
                        )}{" "}
                        <i>(kWh)</i>
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

// Component Properties
TrialBillingSummary.propTypes = {
  // Store objects:
  user: PropTypes.object.isRequired,
  apl: PropTypes.object.isRequired,
  billing: PropTypes.object.isRequired,
  // Store object functions:
  updateBillingWizardBillInProgress: PropTypes.func.isRequired,
  handleBillingProcessSubmission: PropTypes.func.isRequired,
};

// Component State
function TrialBillingSummaryState(state) {
  return {
    user: state.user,
    apl: state.apl,
    billing: state.billing,
  };
}
export default connect(TrialBillingSummaryState, {
  handleBillingProcessSubmission,
  updateBillingWizardBillInProgress,
})(TrialBillingSummary);
